import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? 'form' : 'error'); // 'form', 'success', 'error'
  const [error, setError] = useState(token ? '' : 'Invalid reset link. No token provided.');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password', null, { 
        params: { token, new_password: password } 
      });
      setStatus('success');
      toast.success('Password reset successfully!');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      setError(typeof errorDetail === 'string' ? errorDetail : 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="reset-password-page">
      <SEO 
        title="Reset Password"
        description="Create a new password for your Twenty Tulips Diaries account."
        pathname="/reset-password"
        noindex={true}
      />
      <div className="max-w-md w-full">
        {status === 'form' && (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mx-auto w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-7 w-7 text-stone-600" />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium mb-2">Create new password</h1>
              <p className="text-stone-600">
                Your new password must be at least 8 characters.
              </p>
            </div>
            
            {/* Form */}
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </div>
          </>
        )}
        
        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mb-4">Password Reset!</h1>
            <p className="text-stone-600 mb-8">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Link to="/login">
              <Button className="btn-primary w-full" data-testid="continue-to-login-button">
                Continue to Sign In
              </Button>
            </Link>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mb-4">Invalid Link</h1>
            <p className="text-stone-600 mb-8">{error}</p>
            <Link to="/forgot-password">
              <Button className="btn-primary w-full">
                Request New Reset Link
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
